import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { notifications } from '@mantine/notifications'
import {
  downloadEtiquetasQR,
  useEquipamentoDelete,
  useEquipamentoDetalhe,
  useEquipamentoUpdate,
} from '../../hooks/useEquipamentos'
import EquipamentoForm from './EquipamentoForm'
import MedicoesSection from './MedicoesSection'
import PlanosCompativeisSection from './PlanosCompativeisSection'
import type { EquipamentoFormData } from './schema'
import styles from '../_shared/CrudPage.module.css'

export default function EquipamentoDetalhePage() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const pmocOrigem = searchParams.get('pmoc')
  const { data: eq, isLoading, error } = useEquipamentoDetalhe(id)
  const update = useEquipamentoUpdate(id)
  const del = useEquipamentoDelete()
  const [editando, setEditando] = useState(false)

  if (isLoading) return <div className={styles.loading}>Carregando...</div>
  if (error || !eq) return <div className={styles.empty}>Equipamento não encontrado.</div>

  async function handleUpdate(data: EquipamentoFormData) {
    try {
      await update.mutateAsync(data)
      notifications.show({ color: 'green', message: 'Equipamento atualizado.' })
      setEditando(false)
    } catch {
      notifications.show({ color: 'red', message: 'Erro ao atualizar equipamento.' })
    }
  }

  async function handleDelete() {
    if (!confirm(`Remover equipamento ${eq?.marca} ${eq?.modelo}?`)) return
    try {
      await del.mutateAsync(id)
      notifications.show({ color: 'green', message: 'Equipamento removido.' })
      navigate('/equipamentos')
    } catch {
      notifications.show({
        color: 'red',
        message: 'Erro ao remover equipamento (talvez tenha OS vinculada).',
      })
    }
  }

  async function imprimirQR() {
    try {
      await downloadEtiquetasQR({ ids: [id], formato: 'zebra_50x30' })
    } catch {
      notifications.show({ color: 'red', message: 'Não foi possível gerar a etiqueta QR.' })
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => navigate(pmocOrigem ? `/pmoc/${pmocOrigem}` : '/equipamentos')}
          >
            ← {pmocOrigem ? 'Voltar ao PMOC' : 'Voltar'}
          </button>
          <h1 className={styles.title}>
            {eq.marca} {eq.modelo}
          </h1>
          <p style={{ margin: 0, color: '#666' }}>
            {eq.tipo.replace(/_/g, ' ')} • QR: {eq.qr_code_token.slice(0, 12)}…
          </p>
        </div>
        {!editando && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              onClick={imprimirQR}
            >
              Imprimir QR
            </button>
            <button type="button" className={styles.btnPrimary} onClick={() => setEditando(true)}>
              Editar
            </button>
            <button
              type="button"
              onClick={handleDelete}
              style={{
                padding: '0.5rem 1rem',
                border: '1px solid #C62828',
                background: 'white',
                color: '#C62828',
                cursor: 'pointer',
                borderRadius: '4px',
              }}
            >
              Remover
            </button>
          </div>
        )}
      </header>

      {editando ? (
        <EquipamentoForm
          defaultValues={{
            empresa: eq.empresa ?? eq.site ?? '',
            tipo: eq.tipo as EquipamentoFormData['tipo'],
            marca: eq.marca,
            modelo: eq.modelo,
            numero_serie: eq.numero_serie ?? '',
            capacidade_btu: eq.capacidade_btu,
            observacoes: eq.observacoes ?? '',
          }}
          onSubmit={handleUpdate}
          isLoading={update.isPending}
          submitLabel="Salvar alterações"
        />
      ) : (
        <section
          style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          }}
        >
          <dl style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '0.5rem 1rem' }}>
            <dt>Tipo</dt>
            <dd>{eq.tipo.replace(/_/g, ' ')}</dd>
            <dt>Marca</dt>
            <dd>{eq.marca}</dd>
            <dt>Modelo</dt>
            <dd>{eq.modelo}</dd>
            {eq.numero_serie && (
              <>
                <dt>Número de série</dt>
                <dd>{eq.numero_serie}</dd>
              </>
            )}
            {eq.capacidade_btu != null && (
              <>
                <dt>Capacidade</dt>
                <dd>
                  {Number(eq.capacidade_btu).toLocaleString('pt-BR')} BTU
                  {eq.capacidade_tr != null &&
                    ` • ${Number(eq.capacidade_tr).toFixed(1)} TR`}
                </dd>
              </>
            )}
            <dt>Status</dt>
            <dd>
              <span
                style={{
                  padding: '0.15rem 0.5rem',
                  borderRadius: '4px',
                  background: eq.ativo ? '#C8E6C9' : '#EEEEEE',
                  color: eq.ativo ? '#1B5E20' : '#616161',
                  fontSize: '0.85rem',
                }}
              >
                {eq.ativo ? 'Ativo' : 'Inativo'}
              </span>
            </dd>
            <dt>QR token</dt>
            <dd style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{eq.qr_code_token}</dd>
            {eq.observacoes && (
              <>
                <dt>Observações</dt>
                <dd>{eq.observacoes}</dd>
              </>
            )}
          </dl>
        </section>
      )}

      {!editando && eq.id && <MedicoesSection equipamentoId={eq.id} />}
      {!editando && eq.id && <PlanosCompativeisSection equipamentoId={eq.id} />}
    </div>
  )
}
